import Env from 'lib/Env';
import AuthService from 'lib/authService';
import { aquaAxiosWithCreds, callAsyncWithRetry } from 'lib/utilityFunctions';
import { ASSIGNMENT_LINK_STATUS } from 'lib/constants';
import { saveGameState } from 'a/actions/persistStateActions';
import {
  getAssignmentLink,
  getCurrentAssignmentId,
  getCurrentAssignment,
  getAssignmentLinkStatus,
} from '../selectors';

/**
 * Posts assignment link status updates to the assignment service
 * @param {Object} statusPayload - payload to send to assignment service
 * Sample payload
 * Note: if inferContentStatuses=true is passed as a query param, contentStatuses can be omitted
  {
    "studentBusinessKey": "string",
    "status":  "TODO"| "STARTED" | "READ" | "HANDED IN",
    "assignmentBusinessKey": "string",
    "contentStatuses": [
      {
        "status": "TODO"| "STARTED" | "READ" | "HANDED IN",
        "contentBusinessKey": "string"
      }
    ]
  }
  Status value logic reflected in Student Home:
  - "TODO" appears in To Do section
  - "STARTED" appears in Past Work section
  - "READ" appears in Past Work section but students can toggle status back to TODO
  - "HANDED IN" appears in Past Work section and marked as formally complete
*/
const postToAssignmentService = (statusPayload) => async () => {
  const asyncRetryFn = async () => {
    await aquaAxiosWithCreds(
      `${Env.ASSIGNMENT_SERVICE_URL}/status?inferContentStatuses=true`,
      {
        method: 'POST',
        data: statusPayload,
      },
    );
  };

  const asyncRetryConfig = {
    logger: async (_, { isDoneRetrying }) => {
      if (isDoneRetrying) {
        console.error(`Failed to send assignment link status: ${statusPayload.status} after 3 attempts.`);
      }
    },
  };

  return callAsyncWithRetry(asyncRetryFn, asyncRetryConfig);
};

// Queues up an assignment link to be started when student lands on Boost
// Changes status to assigned - before starting first level of assignment
export const initAssignmentMode = (payload) => ({
  type: 'INIT_ASSIGNMENT_MODE',
  payload,
});

// Officially start assignment - changes status to inProgress, adds startDate
export const startAssignment = () => ({
  type: 'START_ASSIGNMENT',
});

// Update after completing an assignment link level - save gamestate at this point
export const updateAssignmentProgress = (payload) => ({
  type: 'UPDATE_ASSIGNMENT_PROGRESS',
  payload,
});

// Show/hide global nav
export const showAssignmentNav = (payload) => ({
  type: 'SHOW_ASSIGNMENT_NAV',
  payload,
});

// Save assignment link data to gamestate
// Send status update to assignment service if assignment is complete
export const saveAssignmentProgress = () => async (dispatch, getState) => {
  const state = getState();
  const assignmentLink = getAssignmentLink(state);
  const currentAssignmentId = getCurrentAssignmentId(state);
  const status = getAssignmentLinkStatus(state);

  await dispatch(saveGameState('ASSIGNMENT_PROGRESS', assignmentLink));
  if (status === ASSIGNMENT_LINK_STATUS.COMPLETE) {
    const studentBusinessKey = AuthService.getUserBk();
    const statusPayload = {
      studentBusinessKey,
      status: 'HANDED IN',
      assignmentBusinessKey: currentAssignmentId,
    };

    await dispatch(postToAssignmentService(statusPayload));
  }
};

// Delete finished assignment link data but preserve status, startDate, and endDate
export const deleteSavedAssignmentProgress = () => async (dispatch, getState) => {
  const state = getState();
  const assignmentLink = getAssignmentLink(state);
  const completedAssignmentId = getCurrentAssignmentId(state);
  const { status, startDate, endDate } = getCurrentAssignment(state);

  await dispatch(saveGameState('ASSIGNMENT_PROGRESS', {
    ...assignmentLink,
    [completedAssignmentId]: { status, startDate, endDate },
    currentAssignmentId: '',
  }));
};
